/**
 * Threat CSV / JSON import + export helpers.
 *
 * Mirror of step3-scenarios/scenarioCsv.ts. Header row matches the
 * table's read-mode column labels (ID / STRIDE / Target Asset /
 * Damage IDs / Property / Scenario). Array cell `damage_ids` is
 * joined with `; ` inside quoted cells on export and split back on
 * `;` / newline on import.
 *
 * On import, `compromised_property` is auto-derived from
 * `stride_category` so a round-tripped CSV stays consistent even if
 * the user edits the STRIDE value in the spreadsheet (consumer code
 * also derives on save, so this is a defence-in-depth measure).
 */
import {
  THREAT_COLUMNS_READ,
  derivePropertyFromStride,
  type ThreatRecord,
} from './threatFieldSchema';

const EXPORT_COLUMNS = THREAT_COLUMNS_READ;

const escapeCsv = (raw: unknown): string => {
  if (raw == null) return '';
  if (Array.isArray(raw)) return `"${raw.join('; ').replace(/"/g, '""')}"`;
  const s = String(raw);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/** Trigger a CSV download with `threats_YYYY-MM-DD.csv` filename. */
export function exportThreatsToCsv(threats: ThreatRecord[]): void {
  const header = EXPORT_COLUMNS.map((c) => c.label).join(',');
  const rows = threats.map((t) =>
    EXPORT_COLUMNS.map((c) => escapeCsv((t as Record<string, unknown>)[c.key])).join(','),
  );
  const csv = [header, ...rows].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `threats_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
}

/** Minimal CSV parser — quoted fields with embedded commas + newlines. */
function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.length > 0));
}

/** Header label → field key. Falls back to the slugified header when
 *  there's no exact column-label match. `ID` → `threat_id`,
 *  `STRIDE` → `stride_category`, etc. */
const HEADER_OVERRIDES: Record<string, string> = {
  id: 'threat_id',
  stride: 'stride_category',
  target_asset: 'asset_id',
  asset: 'asset_id',
  damage_ids: 'damage_ids',
  property: 'compromised_property',
  scenario: 'threat_scenario',
};

function headerToKey(header: string): string {
  const col = EXPORT_COLUMNS.find(
    (c) => c.label.toLowerCase() === header.trim().toLowerCase(),
  );
  if (col) return col.key;
  const slug = header.trim().toLowerCase().replace(/\s+/g, '_');
  return HEADER_OVERRIDES[slug] ?? slug;
}

const LIST_KEYS = new Set(['damage_ids']);

/** Parse a CSV file into threat records. Auto-derives
 *  `compromised_property` from `stride_category` so the imported row
 *  is consistent even if the spreadsheet user edited STRIDE without
 *  updating Property. */
export function parseThreatCsv(text: string): ThreatRecord[] {
  const rows = parseCsvText(text);
  if (rows.length === 0) return [];
  const headers = rows[0].map(headerToKey);
  return rows.slice(1).map((row) => {
    const out: Record<string, unknown> = {};
    headers.forEach((key, i) => {
      const raw = row[i] ?? '';
      out[key] = LIST_KEYS.has(key)
        ? raw
            .split(/[;\n]/)
            .map((s) => s.trim())
            .filter(Boolean)
        : raw;
    });
    if (out.stride_category) {
      out.compromised_property = derivePropertyFromStride(String(out.stride_category));
    }
    return out as ThreatRecord;
  });
}

/** Parse a JSON file. Accepts `[…]`, `{ threats: [...] }`, or
 *  legacy `{ threats_identified: [...] }`. */
export function parseThreatJson(text: string): ThreatRecord[] {
  const parsed = JSON.parse(text);
  const apply = (arr: ThreatRecord[]): ThreatRecord[] =>
    arr.map((r) => ({
      ...r,
      compromised_property:
        r.compromised_property || derivePropertyFromStride(r.stride_category),
    }));
  if (Array.isArray(parsed)) return apply(parsed as ThreatRecord[]);
  if (parsed && Array.isArray(parsed.threats)) {
    return apply(parsed.threats as ThreatRecord[]);
  }
  if (parsed && Array.isArray(parsed.threats_identified)) {
    return apply(parsed.threats_identified as ThreatRecord[]);
  }
  throw new Error('JSON must be an array of threats or `{ threats: [...] }`');
}

/** Dispatcher: CSV vs JSON by file extension; JSON-first fallback otherwise. */
export async function parseThreatFile(file: File): Promise<ThreatRecord[]> {
  const text = await file.text();
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'json') return parseThreatJson(text);
  if (ext === 'csv') return parseThreatCsv(text);
  try {
    return parseThreatJson(text);
  } catch {
    return parseThreatCsv(text);
  }
}
